import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { getLocalISODate, buildUserName } from "../bookings/user/utils";
import { getFeedsService, likeFeedService } from "../../../services/feeds";
import { getFeedTypeColor } from "../../../utils/feedUtils";
import { searchSeatsService } from "../../../services/userBookings";
import DashboardHeader from "./DashboardHeader";

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return "Good Morning";
  if (hour < 18) return "Good Afternoon";
  return "Good Evening";
};

const getFullName = (person) => {
  const first = person?.first_name || "";
  const last = person?.last_name || "";
  const fullName = `${first} ${last}`.trim();
  return fullName || person?.name || "System";
};

const getInitials = (person) => {
  const first = person?.first_name?.trim()?.[0] || "";
  const last = person?.last_name?.trim()?.[0] || "";
  const initials = `${first}${last}`.trim();
  return initials || person?.name?.trim()?.[0] || "S";
};

const getAvatarUrl = (person) =>
  person?.profile_image ||
  person?.profileImage ||
  person?.avatar ||
  person?.image ||
  person?.photo ||
  "";

const getLikesCount = (feed) =>
  feed?.likes_count ?? feed?.reactions?.likes ?? 0;

const Dashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const userInfo = useSelector((state) => state.auth.userInfo);
  const usersData = useSelector((state) => state.auth.userInfoData);
  const isSuperAdmin = usersData?.roles?.some(
    (role) => role.name === "Super Admin"
  );
  const workspace = useSelector((state) => state.userBookings.workspace);

  const [feeds, setFeeds] = useState([]);
  const [loadingFeeds, setLoadingFeeds] = useState(false);
  const [avatarErrors, setAvatarErrors] = useState({});
  const [likingFeedIds, setLikingFeedIds] = useState({});

  // Load workspace data if needed
  useEffect(() => {
    dispatch.userBookings.loadWorkspace().catch((err) => {
      console.error("Error loading workspace for dashboard:", err);
    });
  }, [dispatch]);

  // Fetch feeds using the API
  useEffect(() => {
    const fetchFeeds = async () => {
      setLoadingFeeds(true);
      try {
        const response = await getFeedsService(1, 10);
        const items =
          response?.data?.feeds || response?.data || response?.feeds || [];
        setFeeds(Array.isArray(items) ? items : []);
      } catch (err) {
        console.error("Error loading feeds for dashboard:", err);
        setFeeds([]);
      } finally {
        setLoadingFeeds(false);
      }
    };
    fetchFeeds();
  }, []);

  // Extract User Info
  const innerUser = userInfo?.userInfo || userInfo;
  const firstName = innerUser?.first_name || "User";
  const currentUserId = innerUser?._id;
  const userName = useMemo(() => buildUserName(userInfo), [userInfo]);

  const allBookings = useMemo(
    () => workspace.bookings || [],
    [workspace.bookings],
  );
  const sections = useMemo(
    () => workspace.sections || [],
    [workspace.sections],
  );

  const todayStr = getLocalISODate(new Date());

  const myBookings = useMemo(
    () =>
      allBookings.filter(
        (b) =>
          (b.bookedFor === currentUserId || b.bookedFor === userName) &&
          (b.status === "active" || b.status === "reserved"),
      ),
    [allBookings, currentUserId, userName],
  );

  // Check today's seat availability via the search API (source of truth for cancellations)
  const [todayPermanentSeatIds, setTodayPermanentSeatIds] = useState(null);

  useEffect(() => {
    const checkTodayAvailability = async () => {
      const layoutId = workspace.layoutId;
      if (!layoutId) return;
      try {
        const startTime = new Date(`${todayStr}T00:00:00`).toISOString();
        const endTime = new Date(`${todayStr}T23:59:59`).toISOString();
        const response = await searchSeatsService({
          layout_id: layoutId,
          start_time: startTime,
          end_time: endTime,
        });
        const sectionData = response.data?.data || response.data || [];
        const permIds = [];
        if (Array.isArray(sectionData)) {
          sectionData.forEach((sec) => {
            (sec.seats || []).forEach((s) => {
              if (s._id && s.booking_details?.type?.toLowerCase() === "permanent") {
                permIds.push(s._id);
              }
            });
          });
        }
        setTodayPermanentSeatIds(permIds);
      } catch (err) {
        console.error("Error checking today seat availability:", err);
      }
    };
    checkTodayAvailability();
  }, [workspace.layoutId, todayStr]);

  const todayBooking = useMemo(() => {
    // 1. Check for a specific (non-permanent) booking for today
    const specificBooking = myBookings.find((b) => b.date === todayStr);
    if (specificBooking) {
      const sec = sections.find((s) => s.id === specificBooking.sectionId);
      const seat = sec?.seats.find((s) => s.id === specificBooking.seatId);
      return {
        seatLabel: specificBooking.seatNumber || seat?.label || "—",
        sectionName: sec?.name || "Floor Layout",
        seatType: (specificBooking.seatType || seat?.type || "laptop").toUpperCase(),
      };
    }

    // 2. Check permanent booking — but verify it's not cancelled for today
    const permanentBooking = myBookings.find(
      (b) => b.bookingType === "permanent" || b.date === "permanent"
    );
    if (permanentBooking) {
      // If availability loaded and seat is NOT permanent for today → cancelled
      if (
        todayPermanentSeatIds !== null &&
        !todayPermanentSeatIds.includes(permanentBooking.seatId)
      ) {
        return null;
      }
      const sec = sections.find((s) => s.id === permanentBooking.sectionId);
      const seat = sec?.seats.find((s) => s.id === permanentBooking.seatId);
      return {
        seatLabel: permanentBooking.seatNumber || seat?.label || "—",
        sectionName: sec?.name || "Floor Layout",
        seatType: (permanentBooking.seatType || seat?.type || "laptop").toUpperCase(),
      };
    }

    return null;
  }, [myBookings, sections, todayStr, todayPermanentSeatIds]);

  const handleLikeFeed = async (feedId) => {
    if (!feedId) return;

    const targetFeed = feeds.find((feed) => (feed._id || feed.id) === feedId);
    if (!targetFeed || targetFeed.visibility_type?.toUpperCase() !== "PUBLIC")
      return;

    const wasLiked = targetFeed.liked_by_current_user;

    setLikingFeedIds((prev) => ({ ...prev, [feedId]: true }));
    try {
      const response = await likeFeedService(feedId);

      const updatedFeed =
        response?.data?.data ||
        response?.data?.feed ||
        response?.data ||
        response?.feed ||
        {};

      const backendLikes =
        updatedFeed?.likes_count ?? updatedFeed?.reactions?.likes;

      const newLikesCount =
        backendLikes !== undefined
          ? backendLikes
          : wasLiked
            ? Math.max(0, getLikesCount(targetFeed) - 1)
            : getLikesCount(targetFeed) + 1;

      setFeeds((prevFeeds) =>
        prevFeeds.map((feed) =>
          (feed._id || feed.id) === feedId
            ? {
                ...feed,
                liked_by_current_user: !wasLiked,
                likes_count: newLikesCount,
                reactions: {
                  ...(feed.reactions || {}),
                  likes: newLikesCount,
                },
              }
            : feed,
        ),
      );
    } catch (error) {
      console.error("Error toggling like for feed:", error);
    } finally {
      setLikingFeedIds((prev) => ({ ...prev, [feedId]: false }));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-between">
      <div className="max-w-[1200px] w-full mx-auto pt-8 pb-20 px-6 flex-grow">
        {/* Dashboard Header Component with Availability Status */}
        <DashboardHeader
          firstName={firstName}
          userName={userName}
          todayBooking={todayBooking}
          isSuperAdmin={isSuperAdmin}
        />

        {/* Title area */}
        <h2 className="text-2xl font-bold text-[#111111] mb-6 tracking-tight">
          Latest Updates & Announcements
        </h2>

        {/* Feed List Container */}
        <div className="space-y-5">
          {loadingFeeds ? (
            <>
              <div className="bg-white border border-slate-200 rounded-[12px] shadow-sm p-5 md:p-6 animate-pulse">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-slate-200" />
                  <div className="flex-1 space-y-3">
                    <div className="h-4 w-48 bg-slate-200 rounded" />
                    <div className="h-3 w-24 bg-slate-100 rounded" />
                    <div className="h-6 w-3/4 bg-slate-100 rounded" />
                    <div className="h-3 w-full bg-slate-100 rounded" />
                    <div className="h-3 w-5/6 bg-slate-100 rounded" />
                  </div>
                </div>
              </div>
              <div className="bg-white border border-slate-200 rounded-[28px] shadow-sm p-5 md:p-6 animate-pulse">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-slate-200" />
                  <div className="flex-1 space-y-3">
                    <div className="h-4 w-56 bg-slate-200 rounded" />
                    <div className="h-3 w-28 bg-slate-100 rounded" />
                    <div className="h-6 w-2/3 bg-slate-100 rounded" />
                    <div className="h-3 w-full bg-slate-100 rounded" />
                    <div className="h-3 w-4/6 bg-slate-100 rounded" />
                  </div>
                </div>
              </div>
            </>
          ) : feeds.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-[28px] py-16 text-center shadow-sm">
              <div className="w-12 h-12 rounded-full bg-slate-50 flex items-center justify-center mx-auto mb-4 text-slate-300">
                <svg
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                  />
                </svg>
              </div>
              <h3 className="text-base font-bold text-slate-700">
                No updates yet
              </h3>
              <p className="text-sm text-slate-400 mt-1">
                Announcements and messages will appear here.
              </p>
            </div>
          ) : (
            feeds.map((item) => {
              const createdBy =
                item?.created_by ||
                item?.createdBy ||
                item?.created_by_user ||
                {};
              const name = getFullName(createdBy);
              const initials = getInitials(createdBy);
              const avatarUrl = getAvatarUrl(createdBy);
              const feedLink =
                item?.link || item?.url || item?.external_link || "";
              const likesCount = getLikesCount(item);
              const isPublicFeed =
                item?.visibility_type?.toUpperCase() === "PUBLIC";
              const alreadyLiked = Boolean(item?.liked_by_current_user);
              const isLiking = Boolean(likingFeedIds[item._id || item.id]);
              return (
                <article
                  key={item._id || item.id}
                  onClick={() => navigate(`/feeds/${item._id || item.id}`)}
                  className="bg-white border border-slate-200 rounded-2xl p-5 md:p-6 shadow-sm transition-all hover:shadow-md cursor-pointer"
                >
                  {/* Avatar + Name + Date grouped with border-bottom */}
                  <div className="flex items-start justify-between gap-4 pb-4 border-b border-slate-200">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="shrink-0 w-12 h-12 rounded-full border border-slate-200 bg-slate-100 overflow-hidden ring-4 ring-slate-50 flex items-center justify-center text-sm font-bold text-slate-600 uppercase">
                        {avatarUrl && !avatarErrors[item._id || item.id] ? (
                          <img
                            src={avatarUrl}
                            alt={name}
                            className="w-full h-full object-cover"
                            onError={() =>
                              setAvatarErrors((prev) => ({
                                ...prev,
                                [item._id || item.id]: true,
                              }))
                            }
                          />
                        ) : (
                          <span>{initials}</span>
                        )}
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-sm md:text-base font-semibold text-slate-900 truncate">
                          {name}
                        </h3>

                        <div className="mt-0.5 flex items-center gap-3">
                          <p className="text-xs text-slate-500">
                            {moment(item.createdAt).format("DD/MM/YYYY")}
                          </p>

                          {item?.is_edited && (
                            <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full text-amber-700 bg-amber-50 border border-amber-100">
                              Edited
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 justify-end shrink-0">
                      {isPublicFeed ? (
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-100">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="2" y1="12" x2="22" y2="12"></line><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path></svg>
                          Public
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full border border-amber-100">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                          Private
                        </span>
                      )}
                      <span
                        className={`text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full ${getFeedTypeColor(item?.feed_type || item?.type || "ANNOUNCEMENT")}`}
                      >
                        {item?.feed_type || item?.type || "ANNOUNCEMENT"}
                      </span>
                    </div>
                  </div>

                  <div className="mt-4 space-y-3">
                    <div>
                      <h4 className="text-[18px] font-bold text-slate-900 leading-snug">
                        {item.title || "Untitled feed"}
                      </h4>
                    </div>

                    {item.message ? (
                      <div>
                        <p className="text-sm text-slate-700 leading-7 whitespace-pre-wrap">
                          {item.message}
                        </p>
                      </div>
                    ) : null}

                    {item.description ? (
                      <div>
                        <p className="text-sm text-slate-700 leading-7 whitespace-pre-wrap">
                          {item.description}
                        </p>
                      </div>
                    ) : null}

                    {feedLink ? (
                      <div>
                        <a
                          href={feedLink}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-sm font-medium text-blue-600 hover:text-blue-700 break-all underline decoration-blue-200"
                        >
                          {feedLink}
                        </a>
                      </div>
                    ) : null}
                  </div>

                  <div
                    className={`mt-5 pt-4 border-t border-slate-200 flex items-center gap-3 text-xs text-slate-500 ${
                      item.visibility_type === "PUBLIC"
                        ? "justify-between"
                        : "justify-end"
                    }`}
                  >
                    {item.visibility_type === "PUBLIC" && (
                      <button
                        type="button"
                        disabled={isLiking}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleLikeFeed(item._id || item.id);
                        }}
                        className={`inline-flex items-center gap-2 rounded-full px-4 py-2 font-semibold transition-colors ${
                          isLiking
                            ? "bg-slate-100 text-slate-400 cursor-wait"
                            : alreadyLiked
                              ? "bg-red-50 text-red-600 hover:bg-red-100"
                              : "bg-slate-100 text-slate-700 hover:bg-red-50 hover:text-red-600"
                        }`}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill={alreadyLiked ? "currentColor" : "none"}
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                        </svg>
                        <span>
                          {alreadyLiked
                            ? "Liked"
                            : isLiking
                              ? "Wait..."
                              : "Like"}
                        </span>
                        <span className="text-slate-500">({likesCount})</span>
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={() =>
                        navigate("/feeds/" + (item._id || item.id))
                      }
                      className="font-semibold text-slate-900 hover:text-blue-600 transition-colors"
                    >
                      View post
                    </button>
                  </div>
                </article>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
